export const ILLUSTRATOR_INTAKE_CONVERTER =
  'imai-illustrator-pdf-intake' as const;
export const ILLUSTRATOR_INTAKE_CONVERTER_VERSION = '2' as const;
export const ILLUSTRATOR_RASTER_DPI = 180;
export const ILLUSTRATOR_MAX_SOURCE_BYTES = 512 * 1024 * 1024;
export const ILLUSTRATOR_MAX_PDF_BYTES = 512 * 1024 * 1024;
/**
 * Largest source a Convex action validates in memory (512 MiB process limit).
 * Bigger sources are validated from a stream plus ranged reads, and their
 * derivative run gets a larger Trigger machine. Kept well under the limit:
 * full validation holds about 3.7× the source size (#4797, 89 MB sources).
 */
export const ILLUSTRATOR_BUFFERED_SOURCE_MAX_BYTES = 32 * 1024 * 1024;
export const ILLUSTRATOR_LARGE_SOURCE_MACHINE = 'large-2x';
/**
 * Every other intake. Memory follows the artboards, not the file size: a
 * 3.7 MB .ai with one huge artboard was OOM-killed on the default small-1x
 * (0.5 GB) at 180 DPI (MK JET-SET, 2026-10-15). The worker caps a page at
 * ILLUSTRATOR_MAX_PAGE_PIXELS and renders one page at a time to a file, so
 * 2 GB covers the largest page it accepts.
 */
export const ILLUSTRATOR_INTAKE_MIN_MACHINE = 'medium-1x';

/** The derivative worker's machine for a source of `sizeBytes` read bytes. */
export function illustratorIntakeMachine(
  sizeBytes: number | undefined
):
  | typeof ILLUSTRATOR_LARGE_SOURCE_MACHINE
  | typeof ILLUSTRATOR_INTAKE_MIN_MACHINE {
  return (sizeBytes ?? 0) > ILLUSTRATOR_BUFFERED_SOURCE_MAX_BYTES
    ? ILLUSTRATOR_LARGE_SOURCE_MACHINE
    : ILLUSTRATOR_INTAKE_MIN_MACHINE;
}
export const ILLUSTRATOR_MAX_PAGE_COUNT = 50;
export const ILLUSTRATOR_MAX_PAGE_OUTPUT_BYTES = 25 * 1024 * 1024;
// The derived PDF (up to the PDF limit) plus the page renders.
export const ILLUSTRATOR_MAX_TOTAL_OUTPUT_BYTES =
  ILLUSTRATOR_MAX_PDF_BYTES + 300 * 1024 * 1024;
/**
 * Page renders feed the agent's `read` and are attached to delegated tasks, so
 * they must stay viewable: providers refuse an image over 8000 px per side
 * (one such image drops every image in the turn), and the model-preview
 * derivative decodes at most 40 MP (PROVIDER_CONTEXT_MAX_INPUT_PIXELS in
 * trigger/lib/native-image/misc-images.ts). Larger artboards render at a
 * lower DPI; the derived PDF keeps full detail.
 */
export const ILLUSTRATOR_MAX_RASTER_WIDTH = 8_000;
export const ILLUSTRATOR_MAX_RASTER_HEIGHT = 8_000;
export const ILLUSTRATOR_MAX_PAGE_PIXELS = 40_000_000;
export const ILLUSTRATOR_MAX_TOTAL_PIXELS = 256_000_000;
export const ILLUSTRATOR_COMMAND_TIMEOUT_MS = 120_000;
export const ILLUSTRATOR_MAX_PROCESSING_MS = 15 * 60_000;
export const ILLUSTRATOR_SOURCE_DOWNLOAD_TIMEOUT_MS = 300_000;
export const ILLUSTRATOR_PDF_VALIDATION_TIMEOUT_MS = 180_000;

export const ILLUSTRATOR_UPLOAD_MIME_TYPES = [
  'application/illustrator',
  'application/postscript',
  'application/vnd.adobe.illustrator',
  'application/x-adobe-illustrator',
  'application/x-illustrator',
  'image/x-illustrator',
] as const;

const illustratorMimeTypes = new Set<string>(ILLUSTRATOR_UPLOAD_MIME_TYPES);

// Each refusal names the Illustrator steps that fix it. The typed codes and
// the remaining messages live in illustrator_intake_failures.ts.
export const ILLUSTRATOR_PDF_COMPATIBILITY_ERROR =
  "This Illustrator file was saved without PDF compatibility, so IMAI can't read its artboards. In Illustrator, use File > Save As (.ai) with 'Create PDF Compatible File' checked, or File > Save a Copy as Adobe PDF, then upload again.";
export const ILLUSTRATOR_SOURCE_TOO_LARGE_ERROR =
  "This Illustrator file is over 512 MB, so its pages can't be rendered. In Illustrator, use File > Save a Copy with images linked instead of embedded, or split the artboards into smaller files, then upload again.";
export const ILLUSTRATOR_CORRUPT_PDF_ERROR =
  "This Illustrator file's PDF data is corrupt or truncated, often from an interrupted save or upload. Open it in Illustrator, use File > Save As (.ai) with 'Create PDF Compatible File' checked, then upload the new copy.";

/**
 * Intake hashes are bare hex digests (paths, manifest, worker checks), while
 * workspace files store `sha256:<hex>`. Compare and write the source file's
 * contentHash only through these helpers.
 */
export function illustratorStorageContentHash(digest: string): string {
  return `sha256:${digest.replace(/^sha256:/i, '').toLowerCase()}`;
}

export function matchesIllustratorSourceHash(
  contentHash: string | undefined,
  digest: string
): boolean {
  return (
    contentHash !== undefined &&
    illustratorStorageContentHash(contentHash) ===
      illustratorStorageContentHash(digest)
  );
}

export function isIllustratorUploadMimeType(
  mimeType: string | null | undefined
): boolean {
  return illustratorMimeTypes.has((mimeType ?? '').trim().toLowerCase());
}

export function isIllustratorIntakeCandidate(args: {
  filename?: string | null;
  mimeType?: string | null;
}): boolean {
  const filenameIsAi =
    args.filename?.trim().toLowerCase().endsWith('.ai') === true;
  const mimeType = (args.mimeType ?? '').trim().toLowerCase();
  // application/postscript is shared by arbitrary PS/EPS files. It is useful
  // for browser-uploaded Illustrator files only when the filename is .ai.
  if (mimeType === 'application/postscript') return filenameIsAi;
  return filenameIsAi || isIllustratorUploadMimeType(mimeType);
}

function safeDerivativeStem(filename: string): string {
  return (
    filename
      .normalize('NFKD')
      .replace(/[^\x00-\x7F]/g, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[.-]+|[.-]+$/g, '') || 'illustrator-source'
  );
}

export function assertNormalizedAbsoluteIllustratorSourcePath(
  sourcePath: string
): string {
  if (
    !sourcePath.startsWith('/') ||
    sourcePath.includes('\\') ||
    sourcePath.includes('\0')
  ) {
    throw new Error('ILLUSTRATOR_SOURCE_PATH_INVALID');
  }
  const segments = sourcePath.split('/').filter(Boolean);
  if (
    segments.length === 0 ||
    segments.some((segment) => segment === '.' || segment === '..')
  ) {
    throw new Error('ILLUSTRATOR_SOURCE_PATH_INVALID');
  }
  const normalized = `/${segments.join('/')}`;
  if (normalized !== sourcePath) {
    throw new Error('ILLUSTRATOR_SOURCE_PATH_INVALID');
  }
  return normalized;
}

export type IllustratorDerivativePlan = {
  derivativeDir: string;
  pdfPath: string;
  manifestPath: string;
  pages: Array<{ page: number; path: string }>;
};

function dirname(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash <= 0 ? '/' : path.slice(0, slash);
}

function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

function joinPath(parent: string, child: string): string {
  return parent === '/' ? `/${child}` : `${parent}/${child}`;
}

/** The folder name buildIllustratorDerivativePlan gives an intake's outputs. */
const ILLUSTRATOR_DERIVATIVE_DIR_NAME = /\.derivatives-[a-f0-9]{12}$/;

/**
 * Whether a folder name is an Illustrator intake's derivative folder. Drive
 * views hide it (#4813); the agent's listings and paths keep it.
 */
export function isIllustratorDerivativeDirName(name: string): boolean {
  return ILLUSTRATOR_DERIVATIVE_DIR_NAME.test(name);
}

/**
 * A short, stable key for one uploaded file, hashed from its whole workspace
 * file id (FNV-1a, 8 hex characters). It keeps two uploads whose names clean up
 * to the same stem (for example "X Y.ai" and "X-Y.ai") and whose bytes match
 * out of one converted folder, while a retry of the same upload gets the same
 * folder back.
 */
function uploadKey(sourceFileId: string): string {
  if (!sourceFileId) throw new Error('ILLUSTRATOR_DERIVATIVE_PLAN_INVALID');
  let hash = 0x811c9dc5;
  for (let index = 0; index < sourceFileId.length; index += 1) {
    hash ^= sourceFileId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function buildIllustratorDerivativePlan(args: {
  sourcePath: string;
  /** The uploaded file's own id; gives each upload its own converted folder. */
  sourceFileId: string;
  sourceHash: string;
  pageCount: number;
}): IllustratorDerivativePlan {
  return planDerivatives(args, uploadKey(args.sourceFileId));
}

/**
 * The folder plan jobs were reserved with before the per-upload key. The
 * worker still accepts it, so a job reserved just before a deploy (or re-sent
 * after one) is not failed for its folder name. Nothing new is planned with it.
 */
export function buildLegacyIllustratorDerivativePlan(args: {
  sourcePath: string;
  sourceHash: string;
  pageCount: number;
}): IllustratorDerivativePlan {
  return planDerivatives(args, null);
}

function planDerivatives(
  args: { sourcePath: string; sourceHash: string; pageCount: number },
  key: string | null
): IllustratorDerivativePlan {
  const sourcePath = assertNormalizedAbsoluteIllustratorSourcePath(
    args.sourcePath
  );
  if (
    !/^[a-f0-9]{64}$/i.test(args.sourceHash) ||
    !Number.isInteger(args.pageCount) ||
    args.pageCount < 1 ||
    args.pageCount > ILLUSTRATOR_MAX_PAGE_COUNT
  ) {
    throw new Error('ILLUSTRATOR_DERIVATIVE_PLAN_INVALID');
  }
  const parentPath = dirname(sourcePath);
  const filename = basename(sourcePath);
  // A folder name is capped at 255 characters; the key and ".derivatives-<hash>"
  // add 34. Only keyed plans trim, so a legacy plan matches its old folder.
  const cleaned = safeDerivativeStem(filename);
  const stem = key
    ? cleaned.slice(0, 200).replace(/[.-]+$/, '') || cleaned.slice(0, 200)
    : cleaned;
  const derivativeDir = joinPath(
    parentPath,
    `${key ? `${stem}.${key}` : stem}.derivatives-${args.sourceHash.slice(0, 12).toLowerCase()}`
  );
  return {
    derivativeDir,
    pdfPath: joinPath(derivativeDir, `${stem}.pdf`),
    manifestPath: joinPath(derivativeDir, 'manifest.json'),
    pages: Array.from({ length: args.pageCount }, (_, index) => {
      const page = index + 1;
      return {
        page,
        path: joinPath(
          derivativeDir,
          `page-${String(page).padStart(3, '0')}.png`
        ),
      };
    }),
  };
}
